/**
 * Generic ad-tracker / S2S postback example.
 *
 * Works with any URL-postback ad tracker — ClickFlare, Voluum, Binom, RedTrack,
 * ClickMagick, RTC, etc. They all share the same shape:
 *   GET https://<your-tracker>/<path>?click_id=<id>&ct=<event-name>&...
 *
 * The click id is resolved automatically by the SDK and exposed at
 *   window.TagadaPay.tracking.clickId
 * (sourced from URL query params first, then first-party cookies).
 *
 * Configure the CONFIG block below, then install this script on:
 *   - your CHECKOUT step (fires `initiate_checkout`)
 *   - your THANK-YOU step (fires `sale`)
 */

import type { ExampleItem } from './index';

export const adTrackerPostbackExample: ExampleItem = {
    title: '🎯 Ad-tracker postback (ClickFlare / Voluum / Binom / …)',
    code: `// ─── CONFIG ────────────────────────────────────────────────────────────
// 1. Set this to your tracker domain (no trailing slash). Examples:
//      ClickFlare:    https://cft.<your-domain>.com/cf/cv
//      Voluum:        https://<sub>.voluumtrk.com/postback
//      Binom:         https://<sub>.com/click.php   (depending on setup)
//      RedTrack:      https://<sub>.rdtk.io/postback
//      ClickMagick:   https://<sub>.clickmagick.com/p
var POSTBACK_URL = 'https://cft.example.com/cf/cv';

// 2. Map your funnel events to whatever event name your tracker expects.
//    Set a value to null to skip that event entirely.
var EVENT_NAMES = {
  initiateCheckout: 'initiate_checkout',
  purchase: 'sale',
};

// 3. Optional: extra query params to append to every postback. Useful for
//    payouts, sub-ids, or anything else your tracker accepts.
//    Each value can be a static string OR a function returning a string.
var EXTRA_PARAMS = {
  // payout: function(){ return Tagada.order ? String(Tagada.order.paidAmount / 100) : ''; },
  // tx_id:  function(){ return Tagada.order ? Tagada.order.id : ''; },
};

// ─── DO NOT EDIT BELOW ─────────────────────────────────────────────────
function getClickId() {
  // SDK-resolved value (URL > cookie). Always prefer this — the SDK has
  // already inspected every known ad-tracker param/cookie name.
  var t = window.TagadaPay && window.TagadaPay.tracking;
  return (t && t.clickId) || null;
}

function fire(eventKey) {
  var ct = EVENT_NAMES[eventKey];
  if (!ct) return; // event disabled
  var clickId = getClickId();
  if (!clickId) {
    console.log('[AdTracker] No click_id present — skipping ' + eventKey);
    return;
  }
  try {
    var u = new URL(POSTBACK_URL);
    u.searchParams.set('click_id', clickId);
    u.searchParams.set('ct', ct);
    Object.keys(EXTRA_PARAMS).forEach(function(k) {
      try {
        var v = EXTRA_PARAMS[k];
        var resolved = typeof v === 'function' ? v() : v;
        if (resolved !== undefined && resolved !== null && resolved !== '') {
          u.searchParams.set(k, String(resolved));
        }
      } catch (e) { /* ignore single-param failures */ }
    });
    fetch(u.toString(), { method: 'GET', mode: 'no-cors', keepalive: true })
      .catch(function(){ /* swallow — best-effort */ });
    console.log('[AdTracker] Fired ' + ct + ' for click_id=' + clickId);
  } catch (e) {
    console.error('[AdTracker] Failed to fire ' + eventKey + ':', e);
  }
}

// Run on the right page lifecycle:
// - 'Tagada.loaded' fires when both DOM and funnel data are ready (native checkout).
// - Falls back to DOMContentLoaded for environments without the Tagada global.
function onReady(cb) {
  if (window.Tagada && typeof window.Tagada.loaded === 'function') {
    window.Tagada.loaded(cb);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb, { once: true });
  } else {
    cb();
  }
}

onReady(function() {
  // Detect page type. Tagada.pageType is set by the native checkout SDK.
  // For other environments, fall back to URL inspection.
  var pageType = (window.Tagada && window.Tagada.pageType)
    || (/thank|success|order/i.test(location.pathname) ? 'thankyou' : 'checkout');

  if (pageType === 'thankyou' || pageType === 'thank-you') {
    fire('purchase');
  } else {
    fire('initiateCheckout');
  }
});`,
};
